import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Link, NavLink, Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Breadcrumb } from "./Breadcrumb";
import { CommandPalette } from "./CommandPalette";
import { EditableName } from "./EditableName";
import { KeyboardHelp } from "./KeyboardHelp";
import { LeftRail } from "./LeftRail";
import { TopBar } from "./layout/TopBar";
import { useBreadcrumb } from "../hooks/useBreadcrumb";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useAuthControls } from "./useAuthControls";
import { useCharacters } from "../api/hooks/useCharacters";
import { useProject, useUpdateProject } from "../api/hooks/useProjects";
import { useKeyboard } from "../hooks/useKeyboard";
import { useSession } from "../api/hooks/useAuth";
import { useMigrateLocalCreations } from "../api/hooks/useCreations";
import { MIGRATION_SESSION_FLAG } from "@/lib/creations";
import styles from "./AppShell.module.css";

export function AppShell() {
  const { id: projectId } = useParams();
  const projectIdNum = projectId ? Number(projectId) : undefined;
  const { data: project } = useProject(projectIdNum);
  // Characters list is needed only for `g c` resolution. The query is gated by
  // `enabled: projectId > 0` inside the hook, so passing 0 when no project is
  // active is a cheap no-op.
  const characters = useCharacters(projectIdNum ?? 0);
  const segments = useBreadcrumb();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const onProjectsList = !projectId;
  const showInlineRail =
    !onProjectsList && projectIdNum !== undefined && projectIdNum > 0 && breakpoint === "desktop";
  const showDrawerRail =
    !onProjectsList && projectIdNum !== undefined && projectIdNum > 0 && breakpoint !== "desktop";

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // ── Migration boot effect ────────────────────────────────────────────────
  // Drain the localStorage creations cache to the server on the first authed
  // mount of this shell (once per browser tab, guarded by a sessionStorage
  // flag). This is the key seam for the anon→logged-in funnel: a visitor who
  // saves tool outputs while browsing will see them in their /projects feed
  // immediately after signing up or logging in.
  //
  // The effect is fire-and-forget — it does NOT block render. After success,
  // useMigrateLocalCreations invalidates qk.creations so the dashboard
  // refetches and shows the merged server set automatically.
  const session = useSession();
  const migrateLocalCreations = useMigrateLocalCreations();
  const userId = session.data?.id ?? null;
  useEffect(() => {
    // Skip until session has resolved.
    if (session.isLoading) return;
    // Only run for authenticated users.
    if (userId == null) return;

    // One run per browser tab — skip if already migrated this session.
    let alreadyMigrated = false;
    try {
      alreadyMigrated = window.sessionStorage.getItem(MIGRATION_SESSION_FLAG) === "1";
    } catch {
      // sessionStorage unavailable (private-browsing restriction) — skip.
      return;
    }
    if (alreadyMigrated) return;

    // Fire-and-forget: mark the flag on success, leave it unset on failure
    // so the next navigation retries.
    migrateLocalCreations.mutate(undefined, {
      onSuccess: () => {
        try {
          window.sessionStorage.setItem(MIGRATION_SESSION_FLAG, "1");
        } catch {
          // sessionStorage write failed — not fatal. The next mount will retry
          // but that is acceptable given the server deduplicates on client_id.
        }
      },
      onError: (err) => {
        // Migration failure is non-fatal — the next mount retries because the
        // flag isn't set. We still want it observable so we can spot a pattern
        // (e.g. backend regression) before users complain. No-op if Sentry isn't
        // initialized.
        Sentry.captureException(err, { tags: { source: "creations-migration-boot" } });
      },
    });
  // migrateLocalCreations is a stable mutation; userId is the primitive
  // identity of the authed session — fires once per login.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.isLoading, userId]);
  // ── End migration boot effect ─────────────────────────────────────────────

  // ?focus=name handoff from "+ New project" + AppShell-driven renames.
  // We snapshot once on mount/param-change and strip the param so refreshes
  // don't re-trigger. The snapshot drives `initialEditing` on the topbar
  // name; the underlying component lazy-inits so flipping back to false
  // doesn't yank the user mid-edit.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusParam = searchParams.get("focus");
  const focusedNameOnMount = useRef<boolean | null>(null);
  if (focusedNameOnMount.current === null) {
    focusedNameOnMount.current = focusParam === "name";
  }
  useEffect(() => {
    if (focusParam !== "name") return;
    // Strip the param so a refresh doesn't re-trigger. Replace so we don't
    // pollute history.
    const next = new URLSearchParams(searchParams);
    next.delete("focus");
    setSearchParams(next, { replace: true });
  }, [focusParam, searchParams, setSearchParams]);

  const updateProject = useUpdateProject(projectIdNum ?? 0);

  useKeyboard({
    // ⌘K opens the search palette. ⌘P (project switcher) currently reuses the
    // same palette — a dedicated switcher dropdown is a V2 follow-up so we
    // don't ship two near-identical UI surfaces in V1.
    "mod+k": () => setPaletteOpen(true),
    "mod+p": () => setPaletteOpen(true),
    "?": () => setHelpOpen(true),
    "g p": () => navigate("/projects"),
    "g s": () => {
      // No standalone scenes route — `/projects/:id` redirects to the first
      // scene via ProjectPage. Acceptable for V1; spec §9.2 documents this.
      if (projectIdNum && projectIdNum > 0) navigate(`/projects/${projectIdNum}`);
    },
    "g c": () => {
      // Jump to the first character. If none exist, do nothing — keeping the
      // shortcut quiet rather than opening an empty/error editor route.
      const first = characters.data?.[0];
      if (projectIdNum && projectIdNum > 0 && first) {
        navigate(`/projects/${projectIdNum}/characters/${first.id}`);
      }
    },
  });

  const brand = (
    <Link to="/projects" className={styles.brand}>
      shotprompter
    </Link>
  );

  const projectChrome = project ? (
    <>
      <EditableName
        as="span"
        className={styles.projectName}
        value={project.name}
        initialEditing={focusedNameOnMount.current === true}
        onSave={(next) => updateProject.mutate({ name: next })}
        ariaLabel="Rename project"
      />
      <button
        type="button"
        className={styles.switcherCaret}
        aria-label="Switch project"
        onClick={() => setPaletteOpen(true)}
      >
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      <span className={styles.modeBadge}>
        Mode: {project.mode === "prompt_only" ? "Prompt-only" : "Full pipeline"}
      </span>
      <NavLink
        to={`/projects/${projectIdNum}/settings`}
        className={styles.iconBtn}
        aria-label="Project settings"
      >
        Settings
      </NavLink>
    </>
  ) : null;

  const authControls = useAuthControls();
  const trailingActions = (
    <>
      <button
        type="button"
        className={styles.iconBtn}
        aria-label="Command palette (⌘K)"
        onClick={() => setPaletteOpen(true)}
        data-touch-hide
      >
        ⌘K
      </button>
      <button
        type="button"
        className={styles.iconBtn}
        aria-label="Keyboard help"
        onClick={() => setHelpOpen(true)}
        data-touch-hide
      >
        ?
      </button>
      <Link to="/settings" className={styles.iconBtn} aria-label="Adapter settings">
        Settings
      </Link>
      {authControls}
    </>
  );

  return (
    <div className={styles.app}>
      {/* Skip-to-content link for keyboard users. Stays visually hidden
          until focused, at which point it slides in over the topbar.
          Particularly important for touch users on phone, who lose the
          desktop's ⌘K + ? affordances and rely on Tab to navigate. */}
      <a href="#sf-main" className={styles.skipLink}>
        Skip to content
      </a>
      <TopBar
        brand={brand}
        primary={projectChrome ?? undefined}
        drawerContent={
          showDrawerRail && projectIdNum !== undefined ? (
            <LeftRail projectId={projectIdNum} />
          ) : undefined
        }
        trailing={trailingActions}
        drawerAriaLabel={project ? `${project.name} navigation` : "Navigation menu"}
      />
      <Breadcrumb segments={segments} />
      <div className={styles.body}>
        {showInlineRail && projectIdNum !== undefined ? <LeftRail projectId={projectIdNum} /> : null}
        <main id="sf-main" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <KeyboardHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
